import { useEffect, useState } from 'react'
import { Trash2, CheckSquare, Square, CheckCircle2, XCircle, Loader2, Clock } from 'lucide-react'
import { api } from '../../api/client'
import type { Message } from '../../api/types'
import { EmptyState } from '../../components/EmptyState'
import { PageHeader } from '../../components/PageHeader'
import { useI18n } from '../../i18n'

function StatusBadge({ status }: { status: string }) {
  switch (status) {
    case 'sent':
      return <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2.5 py-0.5 text-xs font-medium text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400"><CheckCircle2 size={12}/>已发送</span>
    case 'failed':
      return <span className="inline-flex items-center gap-1 rounded-full bg-red-50 px-2.5 py-0.5 text-xs font-medium text-red-700 dark:bg-red-900/30 dark:text-red-400"><XCircle size={12}/>发送失败</span>
    case 'sending':
      return <span className="inline-flex items-center gap-1 rounded-full bg-blue-50 px-2.5 py-0.5 text-xs font-medium text-blue-700 dark:bg-blue-900/30 dark:text-blue-400"><Loader2 size={12} className="animate-spin"/>发送中</span>
    case 'queued':
      return <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-2.5 py-0.5 text-xs font-medium text-amber-700 dark:bg-amber-900/30 dark:text-amber-400"><Clock size={12}/>排队中</span>
    default:
      return <span className="inline-flex items-center gap-1 rounded-full bg-muted px-2.5 py-0.5 text-xs font-medium text-muted-foreground">{status}</span>
  }
}

export function SentPage() {
  const { t } = useI18n()
  const [messages, setMessages] = useState<Message[]>([])
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [error, setError] = useState('')

  const load = () => api.get<Message[]>('/messages?folder=sent').then((data) => { setMessages(data); setSelected(new Set()) }).catch((e: Error) => setError(e.message))
  useEffect(() => { load() }, [])

  useEffect(() => {
    const hasPending = messages.some((m) => m.status === 'queued' || m.status === 'sending')
    if (!hasPending) return
    const timer = setInterval(load, 5000)
    return () => clearInterval(timer)
  }, [messages])

  function toggleSelect(id: string) {
    setSelected((prev) => { const next = new Set(prev); next.has(id) ? next.delete(id) : next.add(id); return next })
  }
  function toggleAll() {
    if (selected.size === messages.length) setSelected(new Set())
    else setSelected(new Set(messages.map((m) => m.id)))
  }
  async function deleteSelected() {
    if (selected.size === 0) return
    if (!confirm(`${t('common.delete')} ${selected.size}?`)) return
    try { await api.post('/messages/batch-delete', { ids: [...selected] }); load() }
    catch (e) { setError(e instanceof Error ? e.message : 'error') }
  }
  async function deleteOne(id: string) {
    try { await api.delete(`/messages/${id}`); load() }
    catch (e) { setError(e instanceof Error ? e.message : 'error') }
  }

  return <>
    <PageHeader title="user.sent" action={selected.size > 0 ? <button onClick={deleteSelected} className="flex h-10 items-center gap-2 rounded-md bg-destructive px-4 text-sm font-medium text-destructive-foreground"><Trash2 size={16}/>{selected.size}</button> : undefined} />
    {error && <p className="mb-4 text-sm text-destructive">{error}</p>}
    {messages.length === 0 ? <EmptyState title="user.emptyMailbox" /> : (
      <div className="panel divide-y overflow-hidden">
        <div className="flex items-center gap-3 px-4 py-2 border-b bg-muted/20">
          <button onClick={toggleAll}>{selected.size === messages.length ? <CheckSquare size={16}/> : <Square size={16}/>}</button>
          <span className="text-xs text-muted-foreground">{messages.length}</span>
        </div>
        {messages.map((message) => (
          <article key={message.id} className="flex items-start gap-3 p-4 transition-colors hover:bg-muted/30">
            <button onClick={() => toggleSelect(message.id)} className="mt-1 shrink-0">{selected.has(message.id) ? <CheckSquare size={16}/> : <Square size={16}/>}</button>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <p className="font-medium truncate">{message.recipients.join(', ')}</p>
                <StatusBadge status={message.status} />
              </div>
              <p className="mt-1 text-sm text-foreground/80">{message.subject || '(No subject)'}</p>
              <p className="mt-1 text-xs text-muted-foreground">{new Date(message.createdAt).toLocaleString()}</p>
            </div>
            <button onClick={() => deleteOne(message.id)} className="shrink-0 rounded border p-1.5 text-destructive hover:bg-destructive/10"><Trash2 size={14}/></button>
          </article>
        ))}
      </div>
    )}
  </>
}
